// MATERIAL UI
import {
  Alert,
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";

// INTERFACES
import { Token } from "./CryptoWithdrawalModal";

// REDUX
import { useWithdrawalSlice } from "src/store/features/withdrawal.slice";
import { useGetNotabeneTransferQuery } from "src/store/features/api/notabene";
import { getCopy } from "src/components/transfers/copy";

// STYLES
import { StyledReviewDetails, StyledReviewTitle } from "../common/styles/CryptoWithdrawalStyled";

interface ICryptoWithdrawalReviewProps {
  token: Token | null;
  withdrawalAddress: string | null;
  withdrawalAmount: number;
  memo?: string;
  isCompleted?: boolean;
  /**
   * D8 — when the persisted transfer's `counterpartyMismatch` is true, the
   * Review surface shows a warning + acknowledgement checkbox; the parent
   * modal's Withdraw button is gated on the checkbox via
   * `onMismatchAckChange`.
   */
  onMismatchAckChange?: (ack: boolean) => void;
}

/**
 * The CryptoWithdrawalStageReview component.
 * This component is used to render the review and completed stage of the withdrawal process.
 *
 * @function
 *
 * @property {Token | null} - The given token, e.g. BTC
 * @property {string | null} withdrawalAddress - The selected address.
 * @property {number} withdrawalAmount - The withdrawal amount.
 * @property {string | null} memo - The memo.
 *
 * @returns {JSX.Element} The JSX code to render the CryptoWithdrawalStageThree component.
 */
export const CryptoWithdrawalStageReview = ({
  token,
  withdrawalAddress,
  withdrawalAmount,
  memo,
  onMismatchAckChange,
}: ICryptoWithdrawalReviewProps): JSX.Element => {
  const { notabeneTxId } = useWithdrawalSlice();
  // Skip the GET when there's no txId (legacy flows that bypass Notabene
  // entirely). When present, we re-fetch on mount to surface any
  // counterparty-mismatch flag that the webhook may have just set.
  const { data: transfer } = useGetNotabeneTransferQuery(notabeneTxId ?? "", {
    skip: !notabeneTxId,
  });
  const counterpartyMismatch = Boolean(transfer?.counterpartyMismatch);

  return (
    <Grid id="withdraw-modal-review">
      <Grid pb={3} container>
        <Grid xs={12} py={1.75} item>
          <FormControl fullWidth>
            <StyledReviewTitle>Token:</StyledReviewTitle>
            {token && (
              <Box display="inline-flex" alignItems="center">
                <StyledReviewDetails>
                  <img
                    src={token.logoUrl || ""}
                    width={16}
                    alt={token.code}
                    style={{ verticalAlign: "middle", marginRight: "8px" }}
                  />
                  {token.displayName}
                  {` (${token.code})`}
                </StyledReviewDetails>
              </Box>
            )}
          </FormControl>
        </Grid>
        <Grid xs={12} py={1.75} item>
          <FormControl fullWidth>
            <StyledReviewTitle>Address:</StyledReviewTitle>
            <StyledReviewDetails>{withdrawalAddress}</StyledReviewDetails>
          </FormControl>
        </Grid>
        <Grid xs={12} py={1.75} item>
          <FormControl fullWidth>
            <StyledReviewTitle>Format/Network:</StyledReviewTitle>
            <StyledReviewDetails>{token?.format || "Unknown"}</StyledReviewDetails>
          </FormControl>
        </Grid>
        <Grid xs={12} py={1.75} item>
          <FormControl fullWidth>
            <StyledReviewTitle>Requested Amount to Withdraw:</StyledReviewTitle>
            <StyledReviewDetails>{withdrawalAmount}</StyledReviewDetails>
          </FormControl>
        </Grid>
        {token?.memo && (
          <Grid xs={12} py={1.75} item>
            <FormControl fullWidth>
              <StyledReviewTitle>Memo:</StyledReviewTitle>
              <StyledReviewDetails>{memo}</StyledReviewDetails>
            </FormControl>
          </Grid>
        )}
        {counterpartyMismatch && (
          <Grid xs={12} py={1.75} item>
            <Alert
              severity="warning"
              data-testid="vasp-mismatch-warning"
              sx={{ borderRadius: "8px", mb: 1 }}>
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                {getCopy(undefined, "vaspMismatchWarning").title}
              </Typography>
              <Typography variant="body2">
                {getCopy(undefined, "vaspMismatchWarning").body}
              </Typography>
            </Alert>
            <FormControlLabel
              control={
                <Checkbox
                  data-testid="vasp-mismatch-ack-checkbox"
                  onChange={(e) => onMismatchAckChange?.(e.target.checked)}
                />
              }
              label="I understand and want to proceed."
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  );
};
